import React, { FC, useEffect, useMemo, useState } from 'react';
import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grow,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FinderHeader } from '@/components/Finder/FinderHeader';
import Container from '@/components/Container';
import SearchField from '@/components/SearchField';
import { MediaResourceType } from '@/declarations/models/MediaResourceType';
import { distinct } from '@/utils/array';
import { Api } from '@/services/Api';
import { useAccessibleMuseumIds } from '@/hooks/useAccessibleMuseumsByApp';
import { Owner } from '@/declarations/models/Owner';

export interface DamsFinderHeaderProps {
  resourceType: MediaResourceType | MediaResourceType[] | '';
  onResourceTypeChanged: (resourceType: MediaResourceType | MediaResourceType[] | '') => void;
  selectableResourceTypes?: Array<MediaResourceType>;
  searchQuery: string;
  onQueryChange: (query: string) => void;
  ekulturMuseumId: number | null;
  onSetEkulturMuseumId: (id: number | null) => void;
}

export const DamsFinderHeader: FC<DamsFinderHeaderProps> = ({
  resourceType,
  onResourceTypeChanged,
  searchQuery,
  onQueryChange,
  selectableResourceTypes,
  ekulturMuseumId,
  onSetEkulturMuseumId,
}) => {
  const defaultResourceTypes = [MediaResourceType.IMAGE, MediaResourceType.VIDEO, MediaResourceType.AUDIO];
  const resourceTypes = selectableResourceTypes ?? defaultResourceTypes;
  const { t: tCommon } = useTranslation('common');
  const { t: tComponents } = useTranslation('components');

  const [owners, setOwners] = useState<Owner[]>([]);

  // Keep Select value as string (MUI Select returns string)
  const [selectedEkulturMuseumId, setSelectedEkulturMuseumId] = useState<string>(
    ekulturMuseumId != null ? String(ekulturMuseumId) : '',
  );

  const DAMS_APP_ID = 'bfb1b23a-97a9-483e-99e2-fa61e4b5b7c6';
  const rawMuseumIds = useAccessibleMuseumIds(DAMS_APP_ID);
  const museumIds = useMemo(() => (rawMuseumIds ?? []).slice().sort((a, b) => a - b), [rawMuseumIds?.join(',')]);

  let selectedResourceType = '';
  if (Array.isArray(resourceType)) {
    selectedResourceType = resourceType[0] || '';
  } else if (typeof resourceType === 'string') {
    selectedResourceType = resourceType;
  }

  const handleOwnerChange = (event: SelectChangeEvent<string>) => {
    const museumIdStr = event.target.value; // '' | '123'
    setSelectedEkulturMuseumId(museumIdStr);

    if (museumIdStr === '') {
      onSetEkulturMuseumId(null);
      return;
    }

    const parsed = Number(museumIdStr);
    onSetEkulturMuseumId(Number.isFinite(parsed) ? parsed : null);
  };

  // Keep local select state in sync with parent value
  useEffect(() => {
    setSelectedEkulturMuseumId(ekulturMuseumId != null ? String(ekulturMuseumId) : '');
  }, [ekulturMuseumId]);

  useEffect(() => {
    if (!museumIds.length) {
      setOwners([]);
      return;
    }

    const ctx = Api.getOwners({ ekultur_museum_ids: museumIds });

    ctx
      .fetchDirect(null)
      .then((res: any) => {
        const nextOwners = Array.isArray(res) ? res[0]?.owners ?? res : res?.owners ?? [];
        setOwners(Array.isArray(nextOwners) ? nextOwners : []);
      })
      .finally(() => ctx.abort?.());
  }, [museumIds]);

  // Auto-select ONLY when there is exactly one available owner.
  // If multiple owners: do nothing (user selection must "stick").
  useEffect(() => {
    if (!owners.length) return;

    const validOwnersWithMuseumId = owners.filter((o) => o.ekultur_museum_id != null);

    if (validOwnersWithMuseumId.length !== 1) {
      return;
    }

    const onlyId = validOwnersWithMuseumId[0].ekultur_museum_id as number;

    if (ekulturMuseumId !== onlyId) {
      setSelectedEkulturMuseumId(String(onlyId));
      onSetEkulturMuseumId(onlyId);
    }
  }, [owners, ekulturMuseumId, onSetEkulturMuseumId]);

  return (
    <FinderHeader
      backgroundColor='#785AAC'
      fontColor='#fff'
      searchField={
        <Container
          gap={2}
          left
          sx={{
            flexGrow: 1,
            flexBasis: '20%',
          }}>
          <SearchField
            round
            onQueryChange={onQueryChange}
            placeholder={tCommon('search')}
            initialValue={searchQuery}
            autoComplete='off'
            id='filter-query-input'
            fullWidth
            size='small'
            sx={{
              maxWidth: searchQuery ? '470px' : '350px',
              transition: 'max-width 300ms ease',
              '&:focus-within': {
                maxWidth: '470px',
              },
            }}
          />
        </Container>
      }
      title={
        <Typography sx={{ fontSize: 'xx-large', fontWeight: 500 }}>{tComponents('Finder.MediaTitleDams')}</Typography>
      }
      topRight={
        <Box sx={{ minWidth: 200 }}>
          <FormControl fullWidth>
            <InputLabel
              id='owner-select-label'
              sx={{
                color: '#000',
                '&.Mui-focused': { color: '#000!important' },
                '&.MuiInputLabel-shrink': { color: '#000' },
              }}>
              {tComponents('Finder.SelectDamsGallery')}
            </InputLabel>

            <Select<string>
              labelId='owner-select-label'
              id='owner-select'
              value={selectedEkulturMuseumId}
              label={tComponents('Finder.SelectDamsGallery')}
              onChange={handleOwnerChange}
              displayEmpty
              renderValue={(val) => {
                if (owners.length === 0) return '';
                if (val === '') return '';

                const match = owners.find((o) => String(o.ekultur_museum_id ?? '') === val);
                return match?.name ?? '';
              }}>
              {owners.map((owner) => {
                const valueStr = owner.ekultur_museum_id != null ? String(owner.ekultur_museum_id) : '';
                return (
                  <MenuItem
                    key={owner.ekultur_museum_id ?? owner.id}
                    value={valueStr}
                    disabled={owner.ekultur_museum_id == null}>
                    {owner.name}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Box>
      }
      bottomRight={
        <>
          {resourceTypes.length > 1 ? (
            <FormControl component='fieldset' color='secondary'>
              <RadioGroup
                aria-labelledby='select-m24-media-type-label'
                sx={{ display: 'flex', flexFlow: 'row wrap' }}
                value={selectedResourceType}
                onChange={(_, v) => onResourceTypeChanged(v as MediaResourceType | '')}>
                <Grow in>
                  <FormControlLabel value='' control={<Radio color='secondary' />} label={tCommon('all')} />
                </Grow>
                {distinct(resourceTypes).map((mediaResourceType, i) => (
                  <Grow key={mediaResourceType} timeout={(i + 1) * 100} in>
                    <FormControlLabel
                      value={mediaResourceType}
                      control={<Radio color='secondary' />}
                      label={tCommon(`MediaResourceType.${mediaResourceType}`)}
                    />
                  </Grow>
                ))}
              </RadioGroup>
            </FormControl>
          ) : (
            <FormLabel id='select-m24-media-type-label' component='legend'>
              {resourceType || resourceTypes.length
                ? tCommon(`MediaResourceType.${resourceType || resourceTypes[0]}`)
                : 'No media type?'}
            </FormLabel>
          )}
        </>
      }
    />
  );
};

export default DamsFinderHeader;
